import { CaseCompare } from './CaseCompare'
import type { ComparePair } from './CaseCompare'
import { marksCanvas } from '../lib/tiles'
import type { Diagnosis } from '../types'

/**
 * 用户这次作业的前后对比：与首页案例用的是同一块板子。
 *
 * 背景：2026-09-11 用户裁决——「还要给处理前后的对比，就和首页一样」。
 *   结果页此前用的是另一块板（ComparePanel）：左右并排两张图，外加一组
 *   「局部 100% / 全幅」的切换。同一件事两套交互，而首页那套明显更能回答
 *   「到底修没修好」——放大镜是原像素，分割线能对齐同一处画面。
 *   并排那块已随本次改动删掉，前后对比整站只此一块。
 * 设计意图：把作业快照翻译成那块板子要的三样东西（两张图、两个尺寸、一个落点），
 *   其余什么都不做。板子那边为此把入参从整条案例收窄成 ComparePair。
 * 关键约束：
 *   1. 落点（放大镜的停靠处）必须由**这次诊断**算出来，不能写死在画面中心。
 *      中心多半落在本来就干净的地方，用户一放大什么都没有，会判定没修。
 *   2. 两侧的尺寸各报各的。走过重画的成品尺寸与原图不同，板子按这个差值
 *      决定要不要说「两张按同一宽度显示」那句话。
 */
export function ResultCompare({ before, after, beforeSrc, afterSrc, diff }: {
  before: Diagnosis
  after: Diagnosis
  beforeSrc: string
  afterSrc: string
  /** 给了就在板子上多一档「差分」。能不能给由调用方判断（见 RipplePage 的 Done）。 */
  diff?: { readings?: { maxLevels: number; meanLevels: number; changedPct: number } }
}) {
  const pair: ComparePair = {
    title: '你这张图',
    before: { src: beforeSrc, pixels: before.pixels },
    after: { src: afterSrc, pixels: after.pixels },
    hotspot: hotspotOf(before),
  }
  // 结果页先给整幅分割线：用户第一眼要比较处理前后整体，不该先看到一块局部放大镜。
  // 放大镜仍在下一颗按钮，判断 2–4 像素纹理时再切过去。
  return <CaseCompare sample={pair} solo diff={diff} initialView="wipe" />
}

/**
 * 放大镜停在哪。
 *
 * 取诊断标出的那些分块：先算它们的重心，再挑离重心最近的**那一格的中心**。
 * 直接用重心不行——问题分成左上右下两片时，重心会落在中间那块干净的地方，
 * 而那正是「放大了什么都没有」的由来。挑最近的一格保证落点确实在标记里。
 *
 * 坐标是相对值（0 到 1）：分块坐标量在归一画布上（`measured_on`），
 * 而板子要的是相对位置，除以画布尺寸即可，不必先映射回原始像素。
 * 一格都没标出来（只有格子纹那一档）时退回画面中心：那一档的纹理铺满全幅，
 * 落在哪儿都看得见。
 */
function hotspotOf(diag: Diagnosis): [number, number] {
  const si = diag.scale_index
  const boxes = si.flagged_tile_boxes
  // 归一画布的尺寸与红框那一屏共用一个解析（measured_on 是 "1280×720" 这样的字符串）。
  const [cw, ch] = marksCanvas(si.measured_on, diag.pixels)
  if (!boxes?.length || !cw || !ch) return [0.5, 0.5]

  const centers = boxes.map(([x, y, w, h]) => [x + w / 2, y + h / 2] as const)
  const gx = centers.reduce((s, c) => s + c[0], 0) / centers.length
  const gy = centers.reduce((s, c) => s + c[1], 0) / centers.length
  let best = centers[0]
  let bestD = Infinity
  for (const c of centers) {
    const d = (c[0] - gx) ** 2 + (c[1] - gy) ** 2
    if (d < bestD) { bestD = d; best = c }
  }
  const clamp = (v: number) => (v < 0.06 ? 0.06 : v > 0.94 ? 0.94 : v)
  return [clamp(best[0] / cw), clamp(best[1] / ch)]
}
